// DialogEconomicActivityListImportFile.tsx (atualizado)

import { useCallback, useEffect, useRef, useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Dialog,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
} from "@mui/material";
import { useEconomicActivityListService } from "../../../../../../../use-cases/economic-activity-list";
import { useAppSelector } from "../../../../../../../application/states/hooks";
import {
  FileUpload as FileUploadIcon,
  Error as ErrorIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { darkenColor } from "../../../../../../../utils";
import { theme } from "../../../../../../styles";
import { useLoadingService } from "../../../../../../../use-cases/loading";
import readExcelFile from "read-excel-file";
import { DialogEconomicActivityListPreview } from "./DialogEconomicActivityListReport";
import { economicActivityListReducer } from "../../../../../../../application/states/economic-activity-list";

// Componente FileUpload (mantido igual)
interface FileUploadProps {
  label?: string;
  onFileSelect: (fileData: {
    name: string;
    size: string;
    columns: string[];
    type: string;
    data: any[];
    mode: string;
  }) => void;
}

export const FileUploadComponent: React.FC<FileUploadProps> = ({
  label,
  onFileSelect,
}) => {
  const inputFileRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);
  const { startLoading, stopLoading } = useLoadingService();
  const economicActivityList = useAppSelector(
    (state) => state.economicActivityList,
  );

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleClick = () => {
    if (inputFileRef.current) {
      inputFileRef.current.click();
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
      e.target.value = "";
    }
  };

  const processFile = async (file: File) => {
    startLoading();

    const fileName = file.name;
    const fileSize = `${Math.round(file.size / 1024)} KB`;
    const fileType = file.type;

    try {
      const excelToJson = (fileData: any[]) => {
        if (!fileData || fileData.length === 0)
          return { data: [], columns: [] };

        const columns = fileData[0];
        const data = fileData.slice(1).map((item) => {
          const obj: any = {};
          columns.forEach((column: any, index: number) => {
            obj[column] = item[index];
          });
          return obj;
        });
        return { data, columns };
      };

      const csvToJson = (csvString: string, delimiter: string) => {
        const rows = csvString.split("\n").filter((row) => row.trim() !== "");
        if (rows.length === 0) return { data: [], columns: [] };

        const headers = rows[0]
          .split(delimiter)
          .map((h) => h.trim().replace(/["']/g, ""));
        const data = rows.slice(1).map((row) => {
          const values = row
            .split(delimiter)
            .map((v) => v.trim().replace(/["']/g, ""));
          const obj: any = {};
          headers.forEach((header, index) => {
            obj[header] = values[index] || "";
          });
          return obj;
        });

        return { data, columns: headers };
      };

      const guessDelimiter = (fileDataString: string) => {
        const delimiters = [",", ";", "\t", "|"];
        let maxCount = 0;
        let maxDelimiter = ",";

        delimiters.forEach((delimiter) => {
          const count = fileDataString.split(delimiter).length;
          if (count > maxCount) {
            maxCount = count;
            maxDelimiter = delimiter;
          }
        });
        return maxDelimiter;
      };

      if (
        fileType ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.name.endsWith(".xlsx") ||
        file.name.endsWith(".xls")
      ) {
        const fileData = await readExcelFile(file, { sheet: 1 });
        const result = excelToJson(fileData);

        onFileSelect({
          data: result.data,
          name: fileName,
          type: fileType,
          size: fileSize,
          columns: result.columns,
          mode:
            economicActivityList.editEconomicActivityListForm.fields.id !== ""
              ? "edit"
              : "create",
        });
        stopLoading();
      } else if (
        fileType === "text/csv" ||
        fileType === "text/plain" ||
        fileType === "application/vnd.ms-excel" ||
        file.name.endsWith(".csv")
      ) {
        const reader = new FileReader();
        reader.onload = (event) => {
          const fileDataString = event.target?.result as string;
          const delimiter = guessDelimiter(fileDataString);
          const result = csvToJson(fileDataString, delimiter);

          onFileSelect({
            data: result.data,
            name: fileName,
            type: fileType,
            size: fileSize,
            columns: result.columns,
            mode:
              economicActivityList.editEconomicActivityListForm.fields.id !== ""
                ? "edit"
                : "create",
          });

          stopLoading();
        };
        reader.onerror = () => {
          stopLoading();
        };
        reader.readAsText(file);
      } else {
        console.error("Formato de arquivo não suportado:", fileType);
        stopLoading();
      }
    } catch (error) {
      console.error("Erro ao processar arquivo:", error);
      stopLoading();
    }
  };

  return (
    <Box
      sx={{
        border: "2px dashed",
        borderColor: dragging ? "primary.dark" : "primary.main",
        borderRadius: 1,
        p: 4,
        textAlign: "center",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        minHeight: 200,
        backgroundColor: dragging ? "action.hover" : "background.paper",
        transition: "all 0.2s ease",
        "&:hover": {
          backgroundColor: "action.hover",
          borderColor: "primary.dark",
        },
      }}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      <FileUploadIcon sx={{ fontSize: 64, color: "primary.main" }} />
      <Typography variant="h6">
        {label || "Arraste e solte o arquivo aqui"}
      </Typography>
      <Typography variant="body2" color="textSecondary">
        ou clique para selecionar
      </Typography>
      <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
        Formatos aceitos: .csv, .xlsx, .xls
      </Typography>
      <input
        ref={inputFileRef}
        type="file"
        hidden
        accept="*/*"
        onChange={handleFileSelect}
      />
    </Box>
  );
};

// Componente ListItemButton estilizado
const ListItemButtonStyled = ({
  children,
  onClick,
}: {
  children: React.ReactNode;
  onClick?: () => void;
}) => {
  return (
    <ListItemButton
      sx={{
        "&:hover": {
          backgroundColor: darkenColor(theme.palette.primary.main, 10),
        },
      }}
      onClick={onClick}
    >
      {children}
    </ListItemButton>
  );
};

// Modal de status da importação
interface ImportStatusModalProps {
  open: boolean;
  type:
    | "success"
    | "error"
    | "warning"
    | "only_repeated"
    | "no_valid_economic_activities";
  title: string;
  message: string;
  selectedColumn?: string;
  totalImported?: number;
  onClose: () => void;
  onConfirm?: () => void;
}

const ImportStatusModal = ({
  open,
  type,
  title,
  message,
  selectedColumn,
  totalImported,
  onClose,
  onConfirm,
}: ImportStatusModalProps) => {
  const getIcon = () => {
    switch (type) {
      case "success":
        return <CheckCircleIcon color="primary" sx={{ fontSize: 100 }} />;
      case "error":
        return <ErrorIcon color="error" sx={{ fontSize: 100 }} />;
      case "warning":
      case "only_repeated":
      case "no_valid_economic_activities":
        return <WarningIcon color="warning" sx={{ fontSize: 100 }} />;
    }
  };

  const getFormattedMessage = () => {
    if (type === "success" && selectedColumn && totalImported) {
      return (
        <>
          <Typography variant="body1" sx={{ mt: 2 }} align="center">
            {message}
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: "action.hover", borderRadius: 1 }}>
            <Typography variant="body2" align="center">
              <strong>Coluna selecionada:</strong> {selectedColumn}
            </Typography>
            <Typography variant="body2" align="center">
              <strong>Total de atividades válidas:</strong>{" "}
              {totalImported.toLocaleString("pt-BR")}
            </Typography>
          </Box>
        </>
      );
    }

    return (
      <Typography variant="body1" sx={{ mt: 2 }} align="center">
        {message}
      </Typography>
    );
  };

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box p={3}>
        <Grid container alignItems="center" flexDirection="column">
          <Grid item>{getIcon()}</Grid>
          <Grid item>
            <Typography
              variant="h4"
              sx={{ mt: 2 }}
              align="center"
              textTransform="uppercase"
              dangerouslySetInnerHTML={{ __html: title }}
            />
          </Grid>
          <Grid item>{getFormattedMessage()}</Grid>
          <Grid item>
            <Box my={3}>
              <Button
                variant="contained"
                sx={{
                  mt: 2,
                  "&:hover": {
                    backgroundColor: darkenColor(
                      theme.palette.primary.main,
                      10,
                    ),
                  },
                }}
                onClick={handleConfirm}
              >
                OK
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};

// Modal de seleção de coluna
interface SelectColumnModalProps {
  open: boolean;
  columns: string[];
  fileName: string;
  onSelect: (column: string) => void;
  onClose: () => void;
}

const SelectColumnModal = ({
  open,
  columns,
  fileName,
  onSelect,
  onClose,
}: SelectColumnModalProps) => {
  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose}>
      <AppBar sx={{ position: "relative" }}>
        <Toolbar>
          <Typography
            sx={{ ml: 2, flex: 1 }}
            textTransform="uppercase"
            variant="h6"
            component="div"
          >
            Selecionar coluna - {fileName}
          </Typography>
          <Button autoFocus color="inherit" onClick={onClose}>
            Fechar
          </Button>
        </Toolbar>
      </AppBar>

      <Box p={3}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Selecione a coluna que contém os códigos das atividades econômicas:
        </Typography>
        <List>
          {columns.map((column, index) => (
            <ListItem key={index} disablePadding>
              <ListItemButtonStyled
                onClick={() => {
                  onSelect(column);
                  onClose();
                }}
              >
                <ListItemText primary={column} />
              </ListItemButtonStyled>
            </ListItem>
          ))}
        </List>
      </Box>
    </Dialog>
  );
};

// Componente principal - MODIFICADO
export const DialogEconomicActivityListImportFile = () => {
  const openImportListEconomicActivity = useAppSelector(
    (state) => state.economicActivityList.openImportListEconomicActivity,
  );
  const economicActivityList = useAppSelector(
    (state) => state.economicActivityList,
  );
  const importModalState = useAppSelector(
    (state) => state.economicActivityList.importModal,
  );

  const [openSelectColumnModal, setOpenSelectColumnModal] = useState(false);
  const [openPreviewModal, setOpenPreviewModal] = useState(false); // <-- NOVO ESTADO

  // Determinar o mode de forma mais robusta
  const getCurrentMode = useCallback(() => {
    // Verificar se estamos em modo de edição
    const isEditMode =
      economicActivityList.editEconomicActivityListForm?.fields?.id &&
      economicActivityList.editEconomicActivityListForm.fields.id !== "";

    return isEditMode ? "edit" : "create";
  }, [economicActivityList.editEconomicActivityListForm?.fields?.id]);

  const {
    setFile,
    setOpenEconomicActivityListImportFile,
    resetEconomicActivityListFileAnalysis,
    resetEconomicActivityListSelectedColumn,
    resetEconomicActivityListFileData,
    resetEconomicActivityListImportModal,
    selectColumn,
    addMultipleEconomicActivitiesToList, // <-- NOVO MÉTODO DA SERVICE
  } = useEconomicActivityListService();

  const handleFileSelect = (fileData: {
    name: string;
    size: string;
    columns: string[];
    type: string;
    data: any[];
    mode: string;
  }) => {
    // Garantir que o mode correto seja passado
    const currentMode = getCurrentMode();

    setFile({
      ...fileData,
      mode: currentMode, // Sobrescrever com o mode atual
    });
  };

  const handleColumnSelect = (column: string) => {
    const currentMode = getCurrentMode();
    selectColumn({
      selectedColumn: column,
      data: economicActivityList.fileData?.data || [],
      mode: currentMode,
    });
  };

  const handleCloseImport = () => {
    resetEconomicActivityListFileAnalysis();
    resetEconomicActivityListSelectedColumn();
    resetEconomicActivityListFileData();
    resetEconomicActivityListImportModal();
    setOpenEconomicActivityListImportFile(false);
    setOpenPreviewModal(false); // <-- Fecha o preview também
  };

  const handleStatusModalClose = () => {
    resetEconomicActivityListImportModal();

    // Se for erro ou warning, limpa os dados do arquivo
    if (
      importModalState?.type === "error" ||
      importModalState?.type === "only_repeated" ||
      importModalState?.type === "no_valid_economic_activities"
    ) {
      resetEconomicActivityListFileData();
    }
  };

  // NOVOS HANDLERS PARA O PREVIEW
  const handleOpenPreview = () => {
    setOpenPreviewModal(true);
  };

  const handlePreviewClose = () => {
    setOpenPreviewModal(false);
  };

  const handlePreviewConfirm = () => {
    // Pega os valores válidos da análise do arquivo
    const validValues = economicActivityList.fileAnalysis?.validValues || [];
    const currentMode = getCurrentMode();

    if (validValues.length > 0) {
      // Chama a service para adicionar os CNAEs válidos à lista
      addMultipleEconomicActivitiesToList({
        economicActivities: validValues,
        mode: currentMode,
      });
    }

    // Fecha o preview e o modal de importação
    setOpenPreviewModal(false);
    handleCloseImport();
  };

  // MODIFICADO: Agora abre o preview em vez de fechar diretamente
  const handleConfirmImport = () => {
    handleOpenPreview();
  };

  return (
    <>
      <Dialog
        fullWidth
        maxWidth="xl"
        open={openImportListEconomicActivity}
        onClose={handleCloseImport}
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <Typography
              sx={{ ml: 2, flex: 1 }}
              textTransform="uppercase"
              variant="h6"
              component="div"
            >
              Importar arquivo de lista de atividades econômicas
            </Typography>
            <Button autoFocus color="inherit" onClick={handleCloseImport}>
              Fechar
            </Button>
          </Toolbar>
        </AppBar>

        <Box p={3}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FileUploadComponent
                onFileSelect={handleFileSelect}
                label="Arraste e solte o arquivo aqui ou clique para selecionar"
              />
            </Grid>

            {economicActivityList.fileData?.name && (
              <>
                <Grid item xs={6}>
                  <List component="div" role="group">
                    <ListItem
                      divider
                      sx={{ cursor: "pointer" }}
                      onClick={() => setOpenSelectColumnModal(true)}
                    >
                      <ListItemText
                        primary="Clique para selecionar a coluna"
                        secondary={
                          economicActivityList.selectedColumn ||
                          "Nenhuma coluna selecionada"
                        }
                      />
                    </ListItem>
                  </List>
                </Grid>

                <Grid item xs={6}>
                  <List component="div" role="group">
                    <ListItem divider>
                      <ListItemText
                        primary="Dados do arquivo"
                        secondary={
                          <>
                            <Typography>
                              <strong>Nome:</strong>{" "}
                              {economicActivityList.fileData.name}
                            </Typography>
                            <Typography>
                              <strong>Tamanho:</strong>{" "}
                              {economicActivityList.fileData.size}
                            </Typography>
                            <Typography>
                              <strong>Tipo:</strong>{" "}
                              {economicActivityList.fileData.type}
                            </Typography>
                            <Typography>
                              <strong>Linhas:</strong>{" "}
                              {economicActivityList.fileData.data?.length.toLocaleString(
                                "pt-BR",
                              ) || 0}
                            </Typography>
                            {economicActivityList.fileAnalysis && (
                              <>
                                <Typography>
                                  <strong>Valores válidos:</strong>{" "}
                                  {economicActivityList.fileAnalysis.totalValidCount.toLocaleString(
                                    "pt-BR",
                                  )}
                                </Typography>
                                <Typography>
                                  <strong>Valores repetidos:</strong>{" "}
                                  {economicActivityList.fileAnalysis.totalRepeatedCount.toLocaleString(
                                    "pt-BR",
                                  )}
                                </Typography>
                              </>
                            )}
                          </>
                        }
                      />
                    </ListItem>
                  </List>
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="primary"
                    onClick={handleConfirmImport}
                    disabled={
                      !economicActivityList.selectedColumn ||
                      economicActivityList.fileAnalysis?.totalValidCount === 0
                    }
                    sx={{
                      "&:hover": {
                        backgroundColor: darkenColor(
                          theme.palette.primary.main,
                          10,
                        ),
                      },
                    }}
                  >
                    Visualizar importação
                  </Button>
                </Grid>
              </>
            )}
          </Grid>
        </Box>
      </Dialog>

      {/* Modal de Status - Renderizado baseado no estado global */}
      {importModalState && (
        <ImportStatusModal
          open={importModalState.open || false}
          type={importModalState.type as any}
          title={importModalState.title || ""}
          message={importModalState.message || ""}
          selectedColumn={importModalState.selectedColumn}
          totalImported={importModalState.totalImported}
          onClose={handleStatusModalClose}
        />
      )}

      {/* Modal de Seleção de Coluna */}
      {economicActivityList.fileData && (
        <SelectColumnModal
          open={openSelectColumnModal}
          columns={economicActivityList.fileData.columns}
          fileName={economicActivityList.fileData.name}
          onSelect={handleColumnSelect}
          onClose={() => setOpenSelectColumnModal(false)}
        />
      )}

      {/* NOVO: Modal de Preview */}
      <DialogEconomicActivityListPreview
        open={openPreviewModal}
        onClose={handlePreviewClose}
        onConfirm={handlePreviewConfirm}
      />
    </>
  );
};